import {
  Building2,
  Handshake,
  HandCoins,
  LayoutGrid,
  ListChecks,
  ListFilter,
  Tags,
  Upload,
  UserSearch,
  Users,
} from 'lucide-react'
import { useRole } from '@/auth/useRole'
import type { NavSection } from '@/layouts/navTypes'
import { WorkspaceLayout } from '@/layouts/WorkspaceLayout'
import { NAV_LABELS } from '@/lib/labels'

/** Grouped sidebar nav (schets-style sections). "Mijn taken" and the profile
 *  block live at the bottom of the sidebar, outside these sections. */
const CRM_SECTIONS: readonly NavSection[] = [
  {
    title: 'Relaties',
    items: [
      { to: '/relaties/overzicht', label: 'Overzicht', icon: LayoutGrid },
      { to: '/relaties/contacten', label: 'Contacten', icon: Users },
      { to: '/relaties/organisaties', label: 'Organisaties', icon: Building2 },
      { to: '/relaties/afspraken', label: 'Afspraken', icon: Handshake },
      { to: '/relaties/lijsten', label: 'Lijsten', icon: ListFilter },
    ],
  },
  // spec-63 (ED-01): rarely used, so below the daily work.
  {
    title: 'Beheer',
    items: [
      { to: '/relaties/importeren', label: 'Importeren', icon: Upload },
      { to: '/relaties/instellingen', label: 'Categorieën en labels', icon: Tags },
    ],
  },
] as const

/** Private fund-leads boards (spec-54): visible to admin + fundraiser only —
 *  deliberately NOT team, the data is private to the fundraiser. */
const FUNDRAISING_SECTION: NavSection = {
  title: 'Fundraising',
  items: [
    { to: '/relaties/fundraising/connecties', label: 'Connecties', icon: UserSearch },
    { to: '/relaties/fundraising/leads', label: 'Leads', icon: HandCoins },
  ],
}

/** The one "Mijn taken" page, inside this module's shell (spec-64 U-011). */
const TASKS_ITEM = { to: '/relaties/taken', label: NAV_LABELS.myTasks, icon: ListChecks } as const

/**
 * Persistent Relaties (CRM, spec-44) shell. The chrome lives in
 * WorkspaceLayout; this component only supplies the nav, so the module stays
 * identical to pre-invest by construction rather than by copy-paste.
 *
 * Nav per role (spec-54): admin sees the Relaties section plus Fundraising; team
 * sees only the Relaties section; the fundraiser sees ONLY Fundraising — the rest
 * of the CRM (and the tasks footer) stays hidden. UX only, backend enforces.
 */
export function RelatiesLayout() {
  const { role } = useRole()
  const navSections =
    role === 'fundraiser'
      ? [FUNDRAISING_SECTION]
      : role === 'admin'
        ? [...CRM_SECTIONS, FUNDRAISING_SECTION]
        : CRM_SECTIONS
  const footerItem = role === 'fundraiser' ? undefined : TASKS_ITEM
  return <WorkspaceLayout navSections={navSections} footerItem={footerItem} />
}
